/**
 * Fias Transfer — sender-side data hooks.
 *
 * TanStack Query per `docs/react-query-patterns.md`; keys come from
 * `queryKeys.fiasTransfer`, never raw string arrays.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, {
  FiasTransferCapabilitySet,
  FiasTransferSpaceListItem,
  FiasTransferSpaceDetailResponse,
  FiasTransferActivityResponse,
  FiasTransferQuoteResponse,
  FiasTransferPageStats,
  FiasTransferScanStatusResponse,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * A holder's transfers — the unarchived list by default, the archive when
 * `archived` is true. Two separate cache entries under one prefix, so
 * archiving a transfer refreshes both lists rather than leaving the one you
 * are not looking at stale.
 */
export function useTransferSpaces(
  holderId: string | null,
  options: { archived?: boolean } = {},
): {
  spaces: FiasTransferSpaceListItem[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const archived = options.archived === true;
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.fiasTransfer.spacesList(holderId ?? '', archived),
    queryFn: () => api.listFiasTransferSpaces(holderId!, { archived }),
    // Idle until a workspace is chosen — the page owns that empty state.
    enabled: !!holderId,
  });

  return {
    spaces: data?.spaces ?? [],
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load transfers' : null,
    refresh: () => void refetch(),
  };
}

export function useTransferSpace(spaceId: string | null): {
  detail: FiasTransferSpaceDetailResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.fiasTransfer.space(spaceId ?? ''),
    queryFn: () => api.getFiasTransferSpace(spaceId!),
    enabled: !!spaceId,
  });

  return {
    detail: data ?? null,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load transfer' : null,
    refresh: () => void refetch(),
  };
}

/**
 * Per-page reading stats for a `pageAnalytics` link.
 *
 * A hook rather than a `useQuery` inside the panel, matching the rest of this
 * arche: the page tests mock `../hooks` and render without a QueryClientProvider,
 * so a component reaching for `useQuery` directly throws
 * "No QueryClient set" and takes the whole page down with it.
 *
 * Returns `null` stats when the link has no page analytics — the caller renders
 * nothing rather than an empty table.
 */
export function useTransferPageStats(linkId: string | null): {
  stats: FiasTransferPageStats | null;
  loading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.fiasTransfer.pageStats(linkId ?? ''),
    queryFn: () => api.getFiasTransferPageStats(linkId!),
    enabled: !!linkId,
    retry: false,
  });
  return { stats: data ?? null, loading: isLoading };
}

export function useTransferLinkActivity(linkId: string | null): {
  activity: FiasTransferActivityResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.fiasTransfer.linkActivity(linkId ?? ''),
    queryFn: () => api.getFiasTransferLinkActivity(linkId!),
    enabled: !!linkId,
  });

  return {
    activity: data ?? null,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load activity' : null,
    refresh: () => void refetch(),
  };
}

/**
 * Live quote for the link builder. Debouncing is the caller's job; this keeps
 * the previous value visible while refetching so the price does not flicker to
 * a spinner every time a toggle moves.
 */
/**
 * Per-document malware-scan state for a space. **Opt-in** — pass `enabled`.
 *
 * Every entry costs an S3 tag read server-side, which is precisely why the
 * manifest and the space detail do not carry verdicts. Fetching this on every
 * dashboard load would put that cost back on the hot path, so the sender asks
 * for it.
 *
 * Not polled either: a scan that is genuinely in flight settles in minutes, and
 * one that never settles is the case this view exists to make visible — a
 * spinner that runs forever communicates less than a state that says PENDING
 * and offers a refresh.
 */
export function useTransferScanStatus(
  spaceId: string | null,
  enabled: boolean,
): {
  status: FiasTransferScanStatusResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.fiasTransfer.scanStatus(spaceId ?? ''),
    queryFn: () => api.getFiasTransferScanStatus(spaceId!),
    enabled: !!spaceId && enabled,
    staleTime: 15_000,
  });
  return {
    status: data ?? null,
    loading: isLoading,
    error: error ? (error instanceof Error ? error.message : String(error)) : null,
    refresh: () => void refetch(),
  };
}

export function useTransferQuote(
  spaceId: string | null,
  params: { capabilities: FiasTransferCapabilitySet; downloadAuthorizations: number },
): { quote: FiasTransferQuoteResponse | null; loading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.fiasTransfer.quote(spaceId ?? '', {
      capabilities: params.capabilities,
      downloadAuthorizations: params.downloadAuthorizations,
    }),
    queryFn: () => api.quoteFiasTransferLink(spaceId!, params),
    enabled: !!spaceId && params.downloadAuthorizations >= 1,
    placeholderData: (prev) => prev,
  });

  return { quote: data ?? null, loading: isLoading };
}

export function useTransferMutations(holderId: string | null) {
  const queryClient = useQueryClient();

  const invalidateSpaces = () => {
    if (holderId) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.fiasTransfer.spaces(holderId) });
    }
  };
  const invalidateSpace = (spaceId: string) =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.fiasTransfer.space(spaceId) });

  const createSpace = useMutation({
    mutationFn: (input: { title: string; message?: string }) =>
      api.createFiasTransferSpace({ holderId: holderId!, ...input }),
    onSuccess: invalidateSpaces,
  });

  const deleteSpace = useMutation({
    mutationFn: (spaceId: string) => api.deleteFiasTransferSpace(spaceId),
    onSuccess: invalidateSpaces,
  });

  const attachDocuments = useMutation({
    mutationFn: (input: { spaceId: string; documentIds: string[] }) =>
      api.attachFiasTransferDocuments(input.spaceId, input.documentIds),
    onSuccess: (_result, input) => invalidateSpace(input.spaceId),
  });

  const detachDocument = useMutation({
    mutationFn: (input: { spaceId: string; documentId: string }) =>
      api.detachFiasTransferDocument(input.spaceId, input.documentId),
    onSuccess: (_result, input) => invalidateSpace(input.spaceId),
  });

  const createLink = useMutation({
    mutationFn: (input: {
      spaceId: string;
      label?: string;
      capabilities?: FiasTransferCapabilitySet;
      downloadAuthorizations: number;
      recipientEmail?: string;
      recipientName?: string;
      password?: string;
      expiresInDays?: number;
    }) => {
      const { spaceId, ...body } = input;
      return api.createFiasTransferLink(spaceId, body);
    },
    onSuccess: (_result, input) => invalidateSpace(input.spaceId),
  });

  const revokeLink = useMutation({
    mutationFn: (input: { linkId: string; spaceId: string }) =>
      api.revokeFiasTransferLink(input.linkId),
    onSuccess: (_result, input) => invalidateSpace(input.spaceId),
  });

  /**
   * Recover a link's share URL on demand.
   *
   * A mutation rather than a query on purpose: the token is a credential, so it
   * is fetched only when the sender asks for it and is never parked in the
   * query cache where an unrelated devtools session or a cache dump would
   * surface it. The server writes an audit row per read, which is the other
   * reason this must not fire on render.
   */
  const revealLinkUrl = useMutation({
    mutationFn: (input: { linkId: string }) => api.getFiasTransferLinkUrl(input.linkId),
  });

  /**
   * File a transfer away, or bring it back.
   *
   * Invalidates the space itself as well as the lists: the detail page shows
   * whether it is archived, and a stale banner there would contradict the list
   * the user just came from.
   */
  const setSpaceArchived = useMutation({
    mutationFn: (input: { spaceId: string; archived: boolean }) =>
      api.setFiasTransferSpaceArchived(input.spaceId, input.archived),
    onSuccess: (_result, input) => {
      invalidateSpaces();
      invalidateSpace(input.spaceId);
    },
  });

  return {
    createSpace,
    deleteSpace,
    attachDocuments,
    detachDocument,
    createLink,
    revokeLink,
    revealLinkUrl,
    setSpaceArchived,
  };
}
